import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@/components/theme-provider'
import { Providers } from '@/components/providers'
import { ProtectedRoute } from '@/components/protected-route'

// Páginas de autenticação
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/app/auth/register/page'
import ForgotPasswordPage from '@/app/auth/forgot-password/page'
import ResetPasswordPage from '@/app/auth/reset-password/page'
import VerifyEmailPage from '@/app/auth/verify-email/page'

// Páginas públicas
import HomePage from '@/pages/HomePage'
import CreateCompanyPage from '@/app/criar-empresa/page'
import ConversasPalatiumPage from '@/app/conversas-palatium/page'

// Páginas do dashboard
import DashboardLayout from '@/layouts/DashboardLayout'
import VisaoGeralOrcamentoPage from '@/pages/dashboard/VisaoGeralOrcamentoPage'
import AnaliseCentroCustoPage from '@/pages/dashboard/AnaliseCentroCustoPage'
import GestaoSaldoMovimentacoesPage from '@/pages/dashboard/GestaoSaldoMovimentacoesPage'
import OrcamentoPage from '@/app/dashboard/orcamento/page'
import RealizadoFinanceiroPage from '@/app/dashboard/orcamento/realizado-financeiro/page'
import OrcamentoSincronizarPage from '@/app/dashboard/orcamento/sincronizar/page'
import OrcamentoAnalisePage from '@/app/dashboard/orcamento/analise/page'
import ValidacaoOrcamentoV2Page from '@/app/dashboard/orcamento/validacao-v2/page'
import OrcamentoTestQueryPage from '@/app/dashboard/orcamento/test-query/page'
import OrcamentoTestarAtualizacaoPage from '@/app/dashboard/orcamento/testar-atualizacao/page'
import CriarDatabasePipefyPage from '@/app/dashboard/pipefy/criar-database/page'
import VincularDatabasesPage from '@/app/dashboard/pipefy/vincular-databases/page'
import PipefyWebhooksPage from '@/app/dashboard/pipefy/webhooks/page'
import DisparadorWebhookPage from '@/app/dashboard/pipefy/disparador-webhook/page'
import AreasPage from '@/app/dashboard/areas/page'
import UsuariosPage from '@/app/dashboard/usuarios/page'
import ConfiguracoesPage from '@/app/dashboard/configuracoes/page'
import NotificacoesPage from '@/app/configuracoes/notificacoes/page'
import AgendaPage from '@/app/dashboard/agenda/page'
import TarefasPage from '@/app/tarefas/page'
import ConversasMarpePage from '@/app/conversas-marpe/page'
import MovimentacaoPage from '@/app/movimentacao/page'
import PipefyPublicFormEmbedPage from '@/pages/PipefyPublicFormEmbedPage'

// Gestor pages
import GestorLayout from '@/layouts/GestorLayout'
import GestorDashboardPage from '@/app/gestor/page'
import GestorDespesasRedirectPage from '@/app/gestor/despesas/page'
import GestorDetalhamentoPage from '@/app/gestor/detalhamento/page'
import GestorRealizadoOrcadoPage from '@/app/gestor/realizado-orcado/page'
import GestorAlertasPage from '@/app/gestor/alertas/page'
import GestorVerbasAdicionaisPage from '@/app/gestor/verbas-adicionais/page'
import GestorMovimentacoesPage from '@/app/gestor/movimentacoes/page'

// Admin pages
import AdminAlunosPage from '@/app/dashboard/admin/alunos/page'
import AdminCursosPage from '@/app/dashboard/admin/cursos/page'
import AdminCursoDetailPage from '@/app/dashboard/admin/cursos/[id]/page'
import AdminCursoAulasPage from '@/app/dashboard/admin/cursos/[id]/aulas/page'
import AdminMatriculasPage from '@/app/dashboard/admin/matriculas/page'

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light" enableSystem={false}>
      <Providers>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/" element={<HomePage />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/criar-empresa" element={<CreateCompanyPage />} />
          <Route path="/movimentacao" element={<MovimentacaoPage />} />
          <Route path="/formulario-pipefy" element={<PipefyPublicFormEmbedPage />} />

          {/* Rotas protegidas - Gestor de Área (/* obrigatório para filhos: /gestor/despesas etc.) */}
          <Route
            path="/gestor/*"
            element={
              <ProtectedRoute>
                <GestorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<GestorDashboardPage />} />
            <Route path="despesas" element={<GestorDespesasRedirectPage />} />
            <Route path="detalhamento" element={<GestorDetalhamentoPage />} />
            <Route path="realizado-orcado" element={<GestorRealizadoOrcadoPage />} />
            <Route path="alertas" element={<GestorAlertasPage />} />
            <Route path="verbas-adicionais" element={<GestorVerbasAdicionaisPage />} />
            <Route path="movimentacoes" element={<GestorMovimentacoesPage />} />
          </Route>

          {/* Rotas protegidas - Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<VisaoGeralOrcamentoPage />} />
            <Route path="orcamento" element={<OrcamentoPage />} />
            <Route path="orcamento/realizado-financeiro" element={<RealizadoFinanceiroPage />} />
            <Route path="orcamento/sincronizar" element={<OrcamentoSincronizarPage />} />
            <Route path="orcamento/analise" element={<OrcamentoAnalisePage />} />
            <Route path="orcamento/validacao-v2" element={<ValidacaoOrcamentoV2Page />} />
            <Route path="orcamento/test-query" element={<OrcamentoTestQueryPage />} />
            <Route path="orcamento/testar-atualizacao" element={<OrcamentoTestarAtualizacaoPage />} />
            <Route path="pipefy/criar-database" element={<CriarDatabasePipefyPage />} />
            <Route path="pipefy/vincular-databases" element={<VincularDatabasesPage />} />
            <Route path="pipefy/webhooks" element={<PipefyWebhooksPage />} />
            <Route path="pipefy/disparador-webhook" element={<DisparadorWebhookPage />} />
            <Route path="areas" element={<AreasPage />} />
            <Route path="centro-custo" element={<AnaliseCentroCustoPage />} />
            <Route path="saldo-movimentacoes" element={<GestaoSaldoMovimentacoesPage />} />
            <Route path="usuarios" element={<UsuariosPage />} />
            <Route path="configuracoes" element={<ConfiguracoesPage />} />
            <Route path="agenda" element={<AgendaPage />} />
            
            {/* Admin routes */}
            <Route path="admin/alunos" element={<AdminAlunosPage />} />
            <Route path="admin/cursos" element={<AdminCursosPage />} />
            <Route path="admin/cursos/:id" element={<AdminCursoDetailPage />} />
            <Route path="admin/cursos/:id/aulas" element={<AdminCursoAulasPage />} />
            <Route path="admin/matriculas" element={<AdminMatriculasPage />} />
          </Route>

          {/* Outras rotas protegidas */}
          <Route
            path="/configuracoes/notificacoes"
            element={
              <ProtectedRoute>
                <NotificacoesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tarefas"
            element={
              <ProtectedRoute>
                <TarefasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conversas-palatium"
            element={
              <ProtectedRoute>
                <ConversasPalatiumPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conversas-marpe"
            element={
              <ProtectedRoute>
                <ConversasMarpePage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Providers>
    </ThemeProvider>
  )
}

export default App

